<script setup lang="ts">
import MsoDetailBody from "@/components/models/practice/detail/MsoDetailBody.vue";
import MsoPlanTrialBanner from "@/components/models/practice/detail/MsoPlanTrialBanner.vue";
import DsoStatsCard from "@/components/models/practice/detail/DsoStatsCard.vue";
import { useAbility } from "@casl/vue";
import { computed } from "vue";
import { useRoute } from "vue-router";
import { definePage } from "unplugin-vue-router/runtime";
import DSOAdminHeader from "@/components/shared/headers/DSOAdminHeader.vue";
import { useMso } from "@/composables/models/practice/useMsos";
import { useMsoCompanies } from "@/composables/models/company/useCompany";
import { useMsoPlan } from "@/composables/models/billing/usePracticePlan";
import { useMsoConnectionCount } from "@/composables/models/practice/useConnectedPractices";

definePage({
  meta: {
    title: "generic.action.viewEntity",
    titleParams: { entity: "generic.entity.mso" },
    checkPrivileges: () => {
      const { can } = useAbility();
      return can($permActions.READ, $permSubjects.PRACTICE__MSO);
    },
  },
});

const route = useRoute();
const { can } = useAbility();
const msoId = computed(() => (route.params as { id: string }).id);
const { data: mso, isLoading } = useMso({ id: msoId.value });

const { companies } = useMsoCompanies(msoId);

const canViewSubscription = computed(
  () =>
    can($permActions.MANAGE_MSO_SUBSCRIPTION, $permSubjects.BILLING__MSOSUBSCRIPTION) ||
    can($permActions.MANAGE_MSO_SUBSCRIPTION_ADMIN, $permSubjects.BILLING__MSOSUBSCRIPTION)
);
const { currentProduct } = useMsoPlan(msoId, canViewSubscription);

const practiceCount = computed(() => companies.value.length);
const officeCount = computed(() =>
  companies.value.reduce((total, company) => total + (company.practice_count ?? 0), 0)
);
const monthly = computed(() => Number(currentProduct.value?.price ?? 0));
const connections = computed(() => currentProduct.value?.conections ?? 0);
const { usedConnections } = useMsoConnectionCount(msoId);
const currency = computed(() => currentProduct.value?.currency ?? "USD");
</script>

<template>
  <div>
    <v-skeleton-loader
      :loading="isLoading"
      type="heading,card"
    >
      <div class="tw:flex tw:flex-col tw:gap-5">
        <DSOAdminHeader :mso="mso" />

        <mso-plan-trial-banner
          v-if="msoId"
          :mso-id="msoId"
        />

        <dso-stats-card
          :practice-count="practiceCount"
          :office-count="officeCount"
          :connections="connections"
          :used-connections="usedConnections"
          :monthly="monthly"
          :currency="currency"
          :mso-id="msoId"
        />

        <mso-detail-body :mso="mso" />
      </div>
    </v-skeleton-loader>
  </div>
</template>
